import { useUser } from "$global-providers/user-provider";
import type {
  FacilityLocationsResponse,
  Location,
} from "$routes/organisation-management/facilities/[:facilityId]/locations/_lib/types";
import { gql, useQuery } from "@apollo/client";
import { createContext, useContext, useMemo } from "react";

export const GET_LOCATIONS_FOR_FACILITY = gql`
  query getFacilityLocations($clinicUuids: [String!]!, $active: Boolean!) {
    facilityLocations(input: { clinicUuids: $clinicUuids, active: $active }) {
      _id
      clinicUuid
      name
      meta {
        created
        updated
      }
    }
  }
`;

type LocationsContextType = {
  refetch: () => void;
  facilityLocations: Location[];
};

export const LocationsContext = createContext<LocationsContextType>({
  refetch: () => null,
  facilityLocations: [],
});

export const useLocationsContext = () => useContext(LocationsContext);

export const LocationsProvider = ({ children }: WithChildren) => {
  const { facilityId } = useUser();

  const { data, refetch } = useQuery<FacilityLocationsResponse>(
    GET_LOCATIONS_FOR_FACILITY,
    {
      variables: { clinicUuids: [facilityId], active: true },
      fetchPolicy: "cache-and-network",
      skip: !facilityId,
    },
  );

  const value = useMemo(() => {
    const facilityLocations =
      data?.facilityLocations
        .slice()
        .sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        ) ?? [];

    return {
      refetch,
      facilityLocations,
    };
  }, [refetch, data?.facilityLocations]);

  return (
    <LocationsContext.Provider value={value}>
      {children}
    </LocationsContext.Provider>
  );
};
